/* The shape of the value line, kept apart from the drawing of it.
 *
 * The chart and the figure above it have to agree on which points fall inside
 * the chosen window and where the holes are. When only the chart knew, the
 * header went on reporting today's move while the chart showed three months —
 * so the arithmetic lives here, once, and both read from it. */

import type { Snapshot } from '../types';

export type ChartMode = 'value' | 'gains' | 'return';
export type RangeKey = '1D' | '1W' | '1M' | '3M' | 'YTD' | 'All' | 'Custom';

export interface CustomRange { from: string; to: string }

export function rangeStart(range: RangeKey, custom: CustomRange, now = new Date()): number {
  const t = now.getTime();
  switch (range) {
    case '1D': return t - 86_400_000;
    case '1W': return t - 7 * 86_400_000;
    case '1M': return t - 30 * 86_400_000;
    case '3M': return t - 90 * 86_400_000;
    case 'YTD': return Date.UTC(now.getUTCFullYear(), 0, 1);
    case 'All': return 0;
    case 'Custom': return new Date(`${custom.from}T00:00:00Z`).getTime();
  }
}

export function rangeEnd(range: RangeKey, custom: CustomRange, now = new Date()): number {
  if (range === 'Custom') return new Date(`${custom.to}T23:59:59Z`).getTime();
  return now.getTime();
}

/** What the buttons are called once they are describing a period rather than
    naming one. `All` needs the date it actually reaches back to, so it is left
    to the caller. */
export const RANGE_LABEL: Record<Exclude<RangeKey, 'All' | 'Custom'>, string> = {
  '1D': 'today',
  '1W': 'past week',
  '1M': 'past month',
  '3M': 'past 3 months',
  'YTD': 'this year',
};

/* How far two consecutive samples may sit apart before the stretch between them
   is a hole rather than a line.
 *
 * This cannot be a constant. The recorded snapshots are hourly, while the line
 * rebuilt from closes is one point per trading day — and a three-hour rule
 * applied to daily points makes every ordinary weekday a gap, which is how a
 * chart with a real close behind every point came to be labelled "no data". So
 * the threshold is read off the series actually on screen: four times its usual
 * step, which clears a weekend on a daily line and a quiet hour on an hourly
 * one. The floor keeps a dense series from tearing on one slow sample; the
 * ceiling keeps two lonely points a year apart from being joined into a trend
 * nobody measured. */
const MIN_GAP_MS = 90 * 60_000;
const MAX_GAP_MS = 6 * 86_400_000;

export function gapLimit(times: number[]): number {
  if (times.length < 2) return Infinity;
  const steps: number[] = [];
  for (let i = 1; i < times.length; i++) steps.push(times[i]! - times[i - 1]!);
  steps.sort((a, b) => a - b);
  const median = steps[Math.floor(steps.length / 2)]!;
  return Math.min(Math.max(median * 4, MIN_GAP_MS), MAX_GAP_MS);
}

/* Whether the market was open at all between two readings.
 *
 * A stretch with no reading in it is not always a hole. Overnight and at the
 * weekend the app is shut and so is the exchange, and nothing could have moved
 * — drawing that as "gap in the record" says data is missing when none is. The
 * value simply stood where it was last measured, which is a fact and can be
 * drawn as one.
 *
 * A stretch that covers a trading session is the other case entirely: the price
 * did move and the book did not see it. That stays a hole.
 *
 * Two things this deliberately gets wrong in the safe direction. A market
 * holiday reads as a trading day, so a Thanksgiving gap is drawn as unknown
 * rather than flat — claiming less than is true, not more. And the session
 * measured is New York's; a book holding a Hong Kong or Paris listing would
 * want the union of its exchanges, which is a change to make when there is one
 * in the book to test it against. §9.1's rule about reading the session from
 * upstream rather than from the clock is about *now*, where a holiday would
 * mislabel a live market; this is about days already past, where the clock is
 * the only record either way. */
const NY = 'America/New_York';
const REGULAR_OPEN_MIN = 9 * 60 + 30;
const REGULAR_CLOSE_MIN = 16 * 60;

const NY_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: NY, hour12: false,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', weekday: 'short',
});

/** The regular session of the New York day containing `ms`, in UTC
    milliseconds. Null at the weekend. */
function regularWindow(ms: number): { open: number; close: number } | null {
  const parts: Record<string, string> = {};
  for (const p of NY_PARTS.formatToParts(new Date(ms))) parts[p.type] = p.value;

  const weekday = parts['weekday'];
  if (weekday === 'Sat' || weekday === 'Sun') return null;

  const y = Number(parts['year']);
  const mo = Number(parts['month']);
  const d = Number(parts['day']);
  // The zone's offset on that date, taken from the instant itself so that
  // summer time needs no table.
  const asUtc = Date.UTC(y, mo - 1, d, Number(parts['hour']) % 24, Number(parts['minute']), Number(parts['second']));
  const offsetMs = asUtc - Math.floor(ms / 1000) * 1000;

  const midnight = Date.UTC(y, mo - 1, d) - offsetMs;
  return {
    open: midnight + REGULAR_OPEN_MIN * 60_000,
    close: midnight + REGULAR_CLOSE_MIN * 60_000,
  };
}

/** True when any regular trading minute falls between the two instants. */
export function tradedBetween(fromMs: number, toMs: number): boolean {
  if (!(toMs > fromMs)) return false;
  // No run of four days misses a weekday, so there is nothing to walk.
  if (toMs - fromMs >= 4 * 86_400_000) return true;

  for (let day = 0; day <= 5; day++) {
    const probe = fromMs + day * 86_400_000;
    if (probe > toMs + 86_400_000) break;
    const win = regularWindow(probe);
    if (!win) continue;
    if (win.close > fromMs && win.open < toMs) return true;
  }
  return false;
}

/** §9.4: hourly for the last seven days, daily before that. */
const RECENT_MS = 7 * 86_400_000;

/** True when the window on screen is wide enough that the chart draws one point
    a day rather than every reading.
 *
 * One rule with two readers. The chart uses it to thin the series; the header
 * uses it to decide whether a scrubbed point is stamped with its hour. Those
 * are the same question asked twice: inside a week every reading is its own
 * point and the hour is a fact about it, and beyond a week one reading stands
 * for a whole day and an hour printed beside it would be describing the moment
 * the app happened to be open rather than the day being pointed at. */
export function perDayResolution(range: RangeKey, custom: CustomRange, now = new Date()): boolean {
  return rangeEnd(range, custom, now) - rangeStart(range, custom, now) > RECENT_MS;
}

/* Thinning to one point a day.
 *
 * The book writes a value on every app open, which is right for keeping a
 * record and wrong for drawing three months of it. Two different symptoms come
 * out of the same cause. The stretch you have been using the app for arrives at
 * a different density from the stretch rebuilt from daily closes, and the seam
 * reads as the book turning volatile on the day you installed it. And on any
 * window wider than a week, today occupies a thirtieth of the screen or less,
 * so every reading taken today stacks into the same few pixels and draws a
 * vertical bar out of what is really a few hours of ordinary movement.
 *
 * So the resolution follows the window being drawn, not the age of the point.
 * A day or a week keeps every reading, which is what those views are for.
 * Anything longer is one point a day throughout — today included, because today
 * is where the readings are densest and the pixels scarcest.
 *
 * Nothing is averaged or smoothed: that would invent a value nobody measured
 * (§9.2). One real reading stands for each day, and a measurement is preferred
 * to arithmetic when a day has both. */
export function atResolution<T extends { at: string; reconstructed?: boolean }>(
  points: T[],
  now = Date.now(),
  /** True when the window on screen is wider than a week. */
  perDayThroughout = false,
): T[] {
  const boundary = perDayThroughout ? Infinity : now - RECENT_MS;
  const recent: T[] = [];
  const perDay = new Map<string, T>();

  for (const p of points) {
    const t = new Date(p.at).getTime();
    if (!Number.isFinite(t)) continue;
    if (t >= boundary) { recent.push(p); continue; }

    const day = p.at.slice(0, 10);
    const held = perDay.get(day);
    if (!held) { perDay.set(day, p); continue; }

    const heldMeasured = held.reconstructed !== true;
    const isMeasured = p.reconstructed !== true;
    if (isMeasured && !heldMeasured) perDay.set(day, p);
    else if (isMeasured === heldMeasured && p.at > held.at) perDay.set(day, p);
  }

  return [...perDay.values(), ...recent].sort((a, b) => a.at.localeCompare(b.at));
}

export interface RangeSummary {
  /** The value the change is measured from, and when it was struck. */
  baseUsd: number;
  baseAt: string;
  /** The base was rebuilt from closing prices, not recorded. Worth saying: it
      answers "what today's holdings were worth then", so the change against it
      is a price move and not the account's own history. */
  baseReconstructed: boolean;
  /* The plain move: what the book was worth then against what it is worth now.
     Kept because it is what the chart's own two ends say, and a figure that
     cannot be traced back to the line is a figure nobody can check. */
  changeUsd: number;
  changePct: number;
  /* Money paid in between the two ends, at the rates that applied.
   *
   * This is the whole reason the pair below exists. Over a year the plain
   * change counts every deposit as a gain: pay in ten thousand and the line
   * climbs by ten thousand without a single share moving, and the percentage
   * reports that as performance. Netting it out is the difference between "the
   * book got bigger" and "the market made money", and only the second is a
   * return. */
  paidInUsd: number;
  /** True when both ends of the window carry a cash-flow record. Where one does
      not, the deposits inside it are unknown — not zero — and the figures below
      fall back to the plain ones rather than netting out a number nobody has. */
  paidInKnown: boolean;
  /** The change with deposits taken out, and that against the capital that was
      actually at work: what stood at the start, plus whatever arrived. */
  marketChangeUsd: number;
  marketChangePct: number;
  points: number;
}

/** The move across a window, measured from the oldest point inside it to what
    the book is worth now. Returns null when the window holds nothing to measure
    from — better to say so than to quote a change against a number that is not
    there. */
export function summariseRange(
  snapshots: Snapshot[],
  range: RangeKey,
  custom: CustomRange,
  latestUsd: number,
  now = new Date(),
  /** What has been paid in altogether, as of now. Needed because every window
      but a closed custom one runs to the live total rather than to a recorded
      point, so the deposits at that end are the book's own figure. */
  latestContributedUsd = 0,
): RangeSummary | null {
  const from = rangeStart(range, custom, now);
  const to = rangeEnd(range, custom, now);
  const inWindow = atResolution(snapshots, now.getTime(), to - from > RECENT_MS)
    .map((s) => ({ s, t: new Date(s.at).getTime() }))
    .filter((p) => Number.isFinite(p.t) && p.t >= from && p.t <= to)
    .sort((a, b) => a.t - b.t);

  const first = inWindow[0];
  if (!first || first.s.totalUsd <= 0) return null;

  /* A closed window ends where the user put it, so the comparison runs to the
     last point inside it rather than to the live total, which is outside. */
  const last = inWindow[inWindow.length - 1]!;
  const endUsd = range === 'Custom' ? last.s.totalUsd : latestUsd;
  const changeUsd = endUsd - first.s.totalUsd;

  /* A snapshot with no contributed figure means the cash flows for that date
     were never recorded, not that nothing had been paid in by then. Treating
     the absence as a zero would make the whole of the book's funding look like
     it arrived inside this window. Both ends have to know, or neither figure
     below is netted. */
  const baseContributed = first.s.contributedUsd;
  const endContributed = range === 'Custom' ? last.s.contributedUsd : latestContributedUsd;
  const paidInKnown = baseContributed > 0 && endContributed > 0;
  const paidInUsd = paidInKnown ? endContributed - baseContributed : 0;

  const marketChangeUsd = changeUsd - paidInUsd;
  /* Against what was at work over the window: what stood at the start, plus
     what arrived during it. Money that came in late did not have the whole
     window to work in, and this does not pretend otherwise — it is a simpler
     figure than a time-weighted return and it is honest about being one. What
     it will not do is call a deposit a gain. */
  const atWork = first.s.totalUsd + Math.max(0, paidInUsd);

  return {
    baseUsd: first.s.totalUsd,
    baseAt: first.s.at,
    baseReconstructed: first.s.reconstructed === true,
    changeUsd,
    changePct: (changeUsd / first.s.totalUsd) * 100,
    paidInUsd,
    paidInKnown,
    marketChangeUsd,
    marketChangePct: atWork > 0 ? (marketChangeUsd / atWork) * 100 : 0,
    points: inWindow.length,
  };
}
